import { State } from '@progress/kendo-data-query';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { AxiosResponse } from 'axios';
import { FC, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import ExportExcel, { defaultExcelOption } from 'src/components/ExportExcel';
import { GridColumn } from 'src/components/GridWrapper';
import ClearFilterButton from 'src/components/design-system/ClearFilterButton';
import { receiptCategories } from 'src/constants/receiptCategories';
import useCompany from 'src/hooks/useCompany';
import { Option, ViewInvoiceListResponse } from 'src/pages/ViewReceipts';
import { SetState } from 'src/types/generic';
import { exportViewReceipts } from 'src/utils/exportToExcel';
import { modifyRequest } from 'src/utils/requestInterceptor';

export const additionalFiltersArray = [
  [
    { field: 'receiptStatus', value: 'Deleted', operator: 'neq' },
    { field: 'receiptStatus', value: 'Cancelled', operator: 'neq' },
  ],
  [],
  [{ field: 'PercentReceived', value: '100', operator: 'lt' }],
  [{ field: 'PercentReceived', value: '100', operator: 'gte' }],
  [{ field: 'receiptStatus', value: 'Completed', operator: 'contains' }],
  [{ field: 'receiptStatus', value: 'Deleted', operator: 'contains' }],
  [{ field: 'isPendingReceiptGoods', value: 'TRUE', operator: 'contains' }],
  [{ field: 'receiptId', value: '0', operator: 'eq' }],
];

interface GridToolbarProps {
  clearFilter: () => void;
  selectionFilter: Option;
  setReceiptDataState: SetState<State>;
  setSelectionFilter: SetState<Option>;
  setGridLoader: SetState<boolean>;
  columns: GridColumn<any>[];
  listCurrentPageReceipts: (
    companyId: string,
    includeReceiptDetails?: boolean,
    clientSide?: boolean
  ) => Promise<AxiosResponse<ViewInvoiceListResponse, any>>;
  isClearFiltersDisabled: boolean;
}

const GridToolbar: FC<GridToolbarProps> = ({
  clearFilter,
  selectionFilter,
  setSelectionFilter,
  setGridLoader,
  columns,
  listCurrentPageReceipts,
  setReceiptDataState,
  isClearFiltersDisabled,
}) => {
  const [excelOption, setExcelOption] = useState(defaultExcelOption);
  const { company } = useCompany();

  const handleExcelChange = useCallback(
    (e: DropDownListChangeEvent) => {
      const selectedOption = e.target.value;
      setExcelOption(selectedOption);
      setGridLoader(true);

      if (selectedOption.value === 1) {
        listCurrentPageReceipts(company.companyId, true, false).then((res) => {
          exportViewReceipts(res.data.items, columns, company.companyName);
          setExcelOption(defaultExcelOption);
          setGridLoader(false);
        });
      } else if (selectedOption.value === 2) {
        listCurrentPageReceipts(company.companyId, true, true)
          .then((res) => {
            exportViewReceipts(res.data.items, columns, company.companyName);
            setExcelOption(defaultExcelOption);
            setGridLoader(false);
          })
          .catch((err) => {
            console.error(err);
            setGridLoader(false);
          });
      }
    },
    [columns, company.companyId, company.companyName, listCurrentPageReceipts, setGridLoader]
  );

  return (
    <div className="flex items-center justify-between w-full">
      <ClearFilterButton onClick={clearFilter} disabled={isClearFiltersDisabled} />

      <div className="flex flex-row gap-4">
        <ExportExcel excelOption={excelOption} onExcelChanged={handleExcelChange} width={200} />
        <DropDownList
          id="view-drop-down-list"
          style={{ width: 300 }}
          data={receiptCategories}
          textField="label"
          dataItemKey="value"
          value={selectionFilter}
          onChange={(e) => {
            setSelectionFilter(e.value as Option);

            setReceiptDataState((prev) => {
              if (modifyRequest(prev).filters.length) {
                toast.info('Preset filter applied. User-applied filters cleared.');
              }
              return {
                ...prev,
                filter: {
                  logic: 'and',
                  filters: [],
                },
              };
            });
          }}
        />
      </div>
    </div>
  );
};
export default GridToolbar;
